<template>
  <div class="gradient" />
  <div class="pool-body">
    <div class="divider" />
    <PoolDeposit v-if="hasPooledFunds" :pool="pool" />

    <PoolRow :label="$t('pools.reserves')">
      {{ reserves2label(pool.reserveX, pool?.coinX) }}
      {{ xTokenEntity?.symbol }} <br />
      {{ reserves2label(pool?.reserveY, pool?.coinY) }}
      {{ yTokenEntity?.symbol }}
    </PoolRow>

    <PoolRow
      v-if="!isVersionV1"
      :label="$t('pools.volume24h')"
      :value="volume24USDToLabel((pool as ITablePool ).volume24USD )"
    />
    <div v-if="isShowBadgeRange" class="badge-wrapper">
      <BadgeRange :in-range="(pool as ITablePoolV1).inRange as boolean" />
    </div>

    <PoolRow :label="$t('pools.apr')" :value="apr2Label(pool?.apr)" />

    <PoolRow
      v-if="!isVersionV1 && hasPooledFunds"
      :label="$t('pools.amountOfFees')"
    >
      {{
        getFees(
          pool.apr as number,
          pool.addedX as number,
          xTokenEntity?.decimals as number,
        )
      }}
      {{ xTokenEntity?.symbol }} <br />

      {{
        getFees(
          pool.apr as number,
          pool.addedY as number,
          yTokenEntity?.decimals as number,
        )
      }}
      {{ yTokenEntity?.symbol }}
    </PoolRow>

    <PoolRow label="TVL" :value="tvl2Label(pool?.tvl)" />
  </div>
  <div class="pool-footer">
    <PButton
      class="p-button-primary pool-footer__button"
      @click="onClick('add-liquidity')"
      >{{ $t('addLiquidity.title') }}</PButton
    >
    <PButton
      v-if="hasPooledFunds"
      class="p-button-secondary pool-footer__button"
      @click="onClick('burn-liquidity')"
      >{{ $t('common.remove') }}</PButton
    >
    <PButton
      class="p-button-secondary pool-footer__button"
      @click="onClick('swap')"
      >{{ $t('swap.swap') }}</PButton
    >
  </div>
</template>

<script setup lang="ts">
import { computed, inject, Ref, ref } from 'vue';

import {
  apr2Label,
  getFees,
  reserves2label,
  tvl2Label,
  volume24USDToLabel,
} from '@/composables/usePoolsTableServices';
import { ITablePool, ITablePoolV1 } from '@/types/pools';
import { BadgeRange } from '@/components/Pools/Table';

import { useTablePoolStore, useTokensStore } from '@/store';
import { PoolRow } from './PoolRow';
import { PoolDeposit } from './PoolDeposit';
import type { DynamicDialogInstance } from 'primevue/dynamicdialogoptions';
import { VERSION_1 } from '@/constants';

type DialogRef = Omit<DynamicDialogInstance, 'data'> & {
  data: {
    pool: ITablePool | ITablePoolV1;
  };
};

type OnAction = (arg: {
  type: 'add-liquidity' | 'burn-liquidity' | 'swap';
  pool: ITablePool | ITablePoolV1;
}) => void;

const tablePoolStore = useTablePoolStore();

const dialogRef = inject<Ref<DialogRef>>('dialogRef');

const pool = dialogRef?.value.data.pool as ITablePool | ITablePoolV1;
const action = dialogRef?.value.close as OnAction;
const isVersionV1 = tablePoolStore.version === VERSION_1;

const tokensStore = useTokensStore();

const hasPooledFunds = computed(() => {
  return Number(pool?.addedX) > 0 || Number(pool?.addedY) > 0;
});

const isShowBadgeRange = ref(
  isVersionV1 && 'inRange' in pool && hasPooledFunds,
);

const xTokenEntity = computed(() => {
  return tokensStore.getToken(pool?.coinX);
});

const yTokenEntity = computed(() => {
  return tokensStore.getToken(pool?.coinY);
});

const onClick = (type: 'add-liquidity' | 'burn-liquidity' | 'swap') => {
  action({ type, pool });
};
</script>

<style lang="scss" scoped src="./style.scss" />
